import { useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { ImpulseSpinner } from 'react-spinners-kit';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGoogleLogin } from '@react-oauth/google';
import {
  createUserWithEmailAndPassword,
  getAuth,
  getIdToken,
  sendEmailVerification,
  signInWithEmailAndPassword } from 'firebase/auth';
import cn from 'classnames';

import authLighthouse from 'assets/AuthLighthouse.png';
import beaconLogoFull from 'assets/BeaconLogoFull.svg';

import { initializeWebDashboardGoogleAuth, refreshAuthToken, setSignedInUser } from 'Redux/Actions/userActions';

import { ROUTES } from 'routes/route.constants';

import { verifyUserEmail, webDashboardLoginUser, webDashboardSignUpUser } from 'services/customLogin';
import { identifyUser } from 'services/eventTracking';

import { logoutUser, parseJwt } from 'utlis/globalFunctions';
import { errorReactToastify } from 'utlis/toasts';
import { isEmail, isPasswordValid } from 'utlis/validators';

import styles from './styles.module.scss';

export default function Auth() {
  const auth = getAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const urlLocation = useLocation();

  const [errorMessage, setErrorMessage] = useState('');
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const USER_TYPES = {
    FIRST_TIME: 'firstTime',
    CUSTOM_AUTH: 'customAuth',
  };

  const [signInStart, setSignInStart] = useState(false);
  const [userType, setUserType] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [signingIn, setSigningIn] = useState(false);

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: onSuccessGoogleLogin,
    onError: resp => console.error('Google login error:', resp),
    select_account: true,
    hosted_domain: '*',
    scope: 'openid email profile',
    include_granted_scopes: false,
  });

  useEffect(() => {
    checkExistingAuth();
  }, []);

  const buttonText = useMemo(() => {
    if (!signInStart) {
      return verifyingEmail ? 'Verifying...' : 'Continue';
    }

    if (userType === USER_TYPES.CUSTOM_AUTH) {
      return signingIn ? 'SIGNING IN...' : 'SIGN IN';
    }

    return signingIn ? 'SIGNING UP...' : 'SIGN UP';
  }, [signInStart, verifyingEmail, userType, signingIn]);

  // Show loader while checking authentication
  if (isCheckingAuth) {
    return (
      <div className={styles['loader-container']}>
        <ImpulseSpinner className="projects__spinner" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:flex lg:w-full min-h-screen">
      {/* Left Column */}
      <div className="bg-white lg:flex-[5] flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md flex flex-col h-full lg:h-full justify-between">
          {/* Logo at top */}
          <div className="pt-8 lg:pt-0 lg:mb-16">
            <img src={beaconLogoFull} alt="onBeacon logo" className="h-auto w-32 max-w-52" />
          </div>

          {/* Sign in form in middle */}
          <div className="flex-1 flex flex-col justify-center space-y-6 lg:flex-none">
            {signInStart && (
              <button
                className="font-inter text-sm font-medium text-gray-900 hover:opacity-70 text-left mb-4"
                onClick={handleGoBack}
              >
                Go back
              </button>
            )}

            <h1 className="font-inter text-3xl font-bold text-gray-900 leading-9 mb-8">
              {signInStart && userType === USER_TYPES.FIRST_TIME ? 'Create a new account' : 'Sign in'}
            </h1>

            {!signInStart && (
              <>
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300
                    bg-white hover:bg-gray-50 transition-colors"
                  disabled={signingIn}
                >
                  <FontAwesomeIcon
                    icon={faGoogle}
                    className="w-5 h-5 opacity-100"
                    style={{ color: '#121723' }}
                  />
                  <span className="font-inter text-sm font-medium text-gray-900 leading-5 ml-8">
                    Sign in with Google
                  </span>
                </button>

                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="font-inter text-sm text-gray-500">
                    OR
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label className="font-inter text-base font-medium text-gray-900 leading-5">
                  Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    className="w-full pl-12 pr-4 py-2.5 border border-gray-300 rounded-lg bg-white font-inter text-base
                     placeholder:text-gray-400 focus:outline-none focus:border-teal-600 focus:ring-3
                     focus:ring-teal-600/10"
                    placeholder="rick.sorkin@email.com"
                    onChange={onEmailChange}
                    onKeyPress={handleKeyPress}
                  />
                </div>
              </div>

              {signInStart && (
                <div className="space-y-2">
                  <label className="font-inter text-base font-medium text-gray-900 leading-5">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    className="w-full pl-12 py-2.5 border border-gray-300 rounded-lg bg-white font-inter text-base
                     placeholder:text-gray-400 focus:outline-none focus:border-teal-600 focus:ring-3
                     focus:ring-teal-600/10"
                    placeholder="Enter your password"
                    onChange={e => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                </div>
              )}

              {userType === USER_TYPES.FIRST_TIME && (
                <ul className="font-inter text-sm text-gray-500 pl-4 space-y-1">
                  <li>
                    8 characters
                  </li>
                  <li>
                    Include lowercase and uppercase
                  </li>
                  <li>
                    Include a number and a special character
                  </li>
                </ul>
              )}

              <button
                className={cn(
                  'w-full flex items-center justify-center px-5 py-2.5 rounded-lg border border-white',
                  'bg-teal-600 hover:bg-teal-700 transition-colors',
                  {
                    'bg-gray-400 hover:bg-gray-400 cursor-not-allowed': verifyingEmail || signingIn,
                  }
                )}
                type="submit"
                disabled={verifyingEmail || signingIn}
              >
                <span className="font-inter text-sm font-medium text-white leading-5">
                  {buttonText}
                </span>
              </button>

              {userType === USER_TYPES.CUSTOM_AUTH && (
                <button
                  type="button"
                  className="font-inter text-sm text-gray-500 hover:text-teal-600 hover:underline p-0
                  border-0 bg-transparent text-left"
                  onClick={handleForgetPassword}
                >
                  Forgot password?
                </button>
              )}
            </form>

            {errorMessage && (
              <p className="font-inter text-sm text-red-500 text-center">
                {errorMessage}
              </p>
            )}
          </div>

          {/* Terms and conditions at bottom */}
          <div
            className="font-inter text-xs font-medium leading-4 text-center pb-8 lg:pb-0 lg:mt-16"
            style={{ color: '#8F97A5' }}
          >
            <span>
              Read our
              {' '}
            </span>
            <a
              href="/terms-and-conditions"
              target="_blank"
              className="hover:opacity-80"
              style={{ color: '#606776' }}
            >
              Terms & Conditions
            </a>
            <span>
              {' '}
              and
              {' '}
            </span>
            <a
              href="/privacy-policy"
              target="_blank"
              className="hover:opacity-80"
              style={{ color: '#606776' }}
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="hidden lg:flex lg:flex-[4] justify-end h-screen	bg-white p-5 items-center">
        <img
          src={authLighthouse}
          alt="Lighthouse"
          className="rounded-xl object-cover h-full w-full"
        />
      </div>
    </div>
  );

  async function checkExistingAuth() {
    try {
      const userObject = localStorage.getItem('userObject');

      if (userObject) {
        const parsedUserObject = JSON.parse(userObject);
        const jwtToken = parseJwt(parsedUserObject.access);

        // Check if token is expired
        if (jwtToken.exp * 1000 < Date.now()) {
          try {
            // Token is expired, try to refresh it
            await dispatch(refreshAuthToken(parsedUserObject.refresh));

            // Preserve URL parameters when navigating back to dashboard
            const dashboardUrl = urlLocation.search ? `${ROUTES.TEAM_HOME}${urlLocation.search}` : ROUTES.TEAM_HOME;

            navigate(dashboardUrl);

            return;
          }
          catch (error) {
            // Refresh failed, clear storage and show auth form
            localStorage.removeItem('userObject');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('authenticated');

            logoutUser(dispatch, navigate);

            // Redirect to team dashboard login screen
            navigate(ROUTES.TEAM_LOGIN);

            return;
          }
        }
        else {
          dispatch(setSignedInUser(parsedUserObject));

          // Preserve URL parameters when navigating back to dashboard
          const dashboardUrl = urlLocation.search ? `${ROUTES.TEAM_HOME}${urlLocation.search}` : ROUTES.TEAM_HOME;

          navigate(dashboardUrl);

          return;
        }
      }
    }
    catch (error) {
      errorReactToastify(
        'Error checking authentication',
        { autoClose: 5000, position: 'bottom-center', style: { backgroundColor: '#FDF0F0', width: '470px' } }
      );
    }
    finally {
      setIsCheckingAuth(false);
    }
  }

  function isValid() {
    if (!email) {
      setErrorMessage('Email is required.');

      return false;
    }
    if (email && !isEmail(email)) {
      setErrorMessage('Please enter a valid Email.');

      return false;
    }

    if (signInStart && !password) {
      setErrorMessage('Password is required.');

      return false ;
    }

    if (signInStart && userType === USER_TYPES.FIRST_TIME && password && !isPasswordValid(password)) {
      setErrorMessage('Password must be 8 characters and include lowercase, uppercase, number and special character');

      return false ;
    }

    setErrorMessage('');

    return true;
  }

  function handleForgetPassword() {
    navigate(ROUTES.TEAM_FORGOT_PASSWORD);
  }

  function onEmailChange(event) {
    setEmail(event.target.value);
  }

  async function checkUserEmail() {
    setVerifyingEmail(true);
    const res = await verifyUserEmail(email);

    if (!res) {
      setErrorMessage('Ops, something went wrong, please try again later');

      return;
    }

    const data = res.data;

    if (data.is_user_exists && data.provider === 'Google') {
      setErrorMessage('Oh! Looks like you created Google account with this email already. Try signing in with that!');
    }
    if (!data.is_user_exists) {
      setSignInStart(true);
      setUserType(USER_TYPES.FIRST_TIME);
    }
    else if (data.is_user_exists && data.provider === 'Firebase') {
      setSignInStart(true);
      setUserType(USER_TYPES.CUSTOM_AUTH);
    }
    setVerifyingEmail(false);
  }

  function handleSubmit(e) {
    e.preventDefault();

    if (!isValid()) {
      return;
    }

    if (!signInStart) {
      checkUserEmail();
    }
    else if (userType === USER_TYPES.FIRST_TIME) {
      signUp();
    }
    else if (userType === USER_TYPES.CUSTOM_AUTH) {
      signIn();
    }
  }

  function handleKeyPress(e) {
    if (e.key === 'Enter' && isValid()) {
      handleSubmit(e);
    }
  }

  function handleGoBack() {
    setSignInStart(false);
    setErrorMessage('');
    setUserType('');
  }

  async function signUp() {
    setSigningIn(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      const user = userCredential.user;

      window.localStorage.setItem('user', JSON.stringify(user));
      window.localStorage.setItem('url', window.location.href);

      await sendEmailVerification(user, {
        url: window.location.href,
        handleCodeInApp: true,
      });

      const idToken = await getIdToken(user);

      await webDashboardSignUpUser(idToken);

      navigate(ROUTES.TEAM_VERIFY_EMAIL);
    }
    catch (error) {
      if (error.message === 'Firebase: Error (auth/email-already-in-use).') {
        setErrorMessage('This email is already in use');
      }
      else if (error.message && error.message !== 'Request failed with status code 400') {
        setErrorMessage(error.message);
      }
      else {
        setErrorMessage('Something went wrong, please try again later');
      }
    }
    setSigningIn(false);
  }

  async function signIn() {
    setSigningIn(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      const user = userCredential.user;

      const idToken = await getIdToken(user);

      if (!user.emailVerified) {
        setErrorMessage('Please verify your account by following instructions in the email you received');
        setSigningIn(false);

        return;
      }

      await webDashboardLoginUser(idToken, dispatch);

      // Redirect to dashboard home after successful login, preserving URL parameters
      const dashboardUrl = urlLocation.search ? `${ROUTES.TEAM_HOME}${urlLocation.search}` : ROUTES.TEAM_HOME;

      navigate(dashboardUrl);
    }

    catch (error) {
      if (error.message === 'Firebase: Error (auth/invalid-login-credentials).') {
        setErrorMessage('Either the email or password don\'t match. Make sure they\'re correct and try again.');
      }
      else {
        setErrorMessage(error.message);
      }
    }
    setSigningIn(false);
  }

  function handleGoogleLogin() {
    googleLogin();
  }

  async function onSuccessGoogleLogin(response) {
    if (!response) {
      return;
    }

    setSigningIn(true);

    const result = await initializeWebDashboardGoogleAuth(response.code, dispatch);

    if (!result) {
      setErrorMessage(
        'Oh! Looks like you created a local account (non-Google authenticated) with that email already. '
        + 'Try signing in!'
      );
      setSigningIn(false);

      return;
    }

    ReactGA.set({ userId: result?.uuid });
    identifyUser(result?.uuid);

    // Preserve URL parameters when navigating to dashboard
    const dashboardUrl = urlLocation.search ? `${ROUTES.TEAM_HOME}${urlLocation.search}` : ROUTES.TEAM_HOME;

    navigate(dashboardUrl);
    setSigningIn(false);
  }
}
